import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { RefreshCw } from 'lucide-react';
import { agenticIaProgramData } from '../agentic-ia/data/agenticIaProgramData';
import { itSalesProgramData } from '../it-sales/data/itSalesProgramData';
import './ProgramTabsSection.css';

const DEFAULT_TAG = 'clevory academy';

const PROGRAM_DATA_REGISTRY = {
	'/formations/agentic-ia/program': agenticIaProgramData,
	'/formations/it-sales/program': itSalesProgramData,
};

const PROGRAM_TRACKS = {
	'/formations/it-sales/program': [
		{ value: '', label: 'Choisir un parcours' },
		{ value: 'IT SALES NIVEAU 1', label: 'It sales - Niveau 1' },
		{ value: 'IT SALES NIVEAU 2', label: 'It sales - Niveau 2' },
		{ value: 'IT SALES LES 2 NIVEAUX', label: 'It sales - Les 2 niveaux' },
	],
	'/formations/agentic-ia/program': [
		{ value: '', label: 'Choisir un parcours' },
		{ value: 'AGENTIC AI NIVEAU 1', label: 'Agentic AI - Niveau 1' },
		{ value: 'AGENTIC AI NIVEAU 2', label: 'Agentic AI - Niveau 2' },
		{
			value: 'AGENTIC AI LES 2 NIVEAUX',
			label: 'Agentic AI - Les 2 niveaux',
		},
	],
};

const LEVEL_TO_TRACK_MAP = {
	'/formations/it-sales/program': {
		niveau1: 'IT SALES NIVEAU 1',
		niveau2: 'IT SALES NIVEAU 2',
	},
	'/formations/agentic-ia/program': {
		niveau1: 'AGENTIC AI NIVEAU 1',
		niveau2: 'AGENTIC AI NIVEAU 2',
	},
};

const studyLevels = [
	{ value: '', label: "Niveau d'études" },
	{ value: 'bac', label: 'bac' },
	{ value: 'bac+2', label: 'bac+2' },
	{ value: 'bac+3', label: 'bac+3' },
	{ value: 'bac+5', label: 'bac+5' },
	{ value: 'autre', label: 'autre' },
];

function generateCaptcha() {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
	return Array.from(
		{ length: 4 },
		() => chars[Math.floor(Math.random() * chars.length)],
	).join('');
}

function getLockedTrackFromLevel(pathname, level) {
	if (!level) return '';
	return LEVEL_TO_TRACK_MAP[pathname]?.[level] || '';
}

async function postJson(url, payload, fallbackErrorMessage) {
	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(payload),
	});

	let data = {};
	try {
		data = await response.json();
	} catch {
		data = {};
	}

	if (!response.ok || data.success === false || data.ok === false) {
		throw new Error(
			data.error ||
				data.message ||
				fallbackErrorMessage ||
				"Erreur lors de l'envoi.",
		);
	}

	return data;
}

export default function ProgramTabsSection() {
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const pathname = location.pathname;
	const dataSource = PROGRAM_DATA_REGISTRY[pathname] || agenticIaProgramData;
	const allProgramTracks = PROGRAM_TRACKS[pathname] || [
		{ value: '', label: 'Choisir un parcours' },
	];

	const levelFromState =
		location.state?.niveau || location.state?.semester || null;

	const levelFromQuery =
		searchParams.get('niveau') || searchParams.get('semester');

	const selectedLevel = levelFromState || levelFromQuery || 'niveau1';
	const hasLevelInUrl = Boolean(levelFromState || levelFromQuery);

	const lockedTrackValue = useMemo(
		() => getLockedTrackFromLevel(pathname, selectedLevel),
		[pathname, selectedLevel],
	);

	const visibleProgramTracks = useMemo(() => {
		if (!hasLevelInUrl || !lockedTrackValue) {
			return allProgramTracks;
		}

		const matchedTrack = allProgramTracks.find(
			(track) => track.value === lockedTrackValue,
		);

		return matchedTrack ? [matchedTrack] : allProgramTracks;
	}, [allProgramTracks, hasLevelInUrl, lockedTrackValue]);

	const defaultTrackValue = useMemo(() => {
		if (hasLevelInUrl && lockedTrackValue) {
			return lockedTrackValue;
		}

		return allProgramTracks[0]?.value || '';
	}, [hasLevelInUrl, lockedTrackValue, allProgramTracks]);

	const programData =
		dataSource[selectedLevel] ||
		dataSource.niveau1 ||
		Object.values(dataSource)[0];

	const [activeTab, setActiveTab] = useState(programData.tabs[0]?.id || '');
	const [openItems, setOpenItems] = useState({});

	const [form, setForm] = useState({
		fullName: '',
		email: '',
		phone: '',
		level: '',
		track: defaultTrackValue,
		captchaInput: '',
	});

	const [errors, setErrors] = useState({});
	const [captcha, setCaptcha] = useState(generateCaptcha());
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitMessage, setSubmitMessage] = useState({
		type: '',
		text: '',
	});

	useEffect(() => {
		if (!programData?.tabs?.length) return;

		setActiveTab(programData.tabs[0].id);

		const initialOpenState = {};
		programData.tabs.forEach((tab) => {
			if (tab.items?.[0]) {
				initialOpenState[tab.items[0].id] = true;
			}
		});

		setOpenItems(initialOpenState);
	}, [selectedLevel, pathname, programData]);

	useEffect(() => {
		setForm((prev) => ({
			...prev,
			track: defaultTrackValue,
			captchaInput: '',
		}));
		setErrors((prev) => ({
			...prev,
			track: '',
			captchaInput: '',
		}));
		setCaptcha(generateCaptcha());
	}, [defaultTrackValue, pathname, selectedLevel]);

	useEffect(() => {
		if (!submitMessage.text) return;

		const timer = setTimeout(() => {
			setSubmitMessage({ type: '', text: '' });
		}, 3500);

		return () => clearTimeout(timer);
	}, [submitMessage]);

	const activeContent = useMemo(() => {
		if (!programData?.tabs?.length) return null;

		return (
			programData.tabs.find((tab) => tab.id === activeTab) ||
			programData.tabs[0]
		);
	}, [activeTab, programData]);

	const toggleAccordion = (itemId) => {
		setOpenItems((prev) => ({
			...prev,
			[itemId]: !prev[itemId],
		}));
	};

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => ({ ...prev, [field]: '' }));
	};

	const refreshCaptcha = () => {
		setCaptcha(generateCaptcha());
		setForm((prev) => ({ ...prev, captchaInput: '' }));
		setErrors((prev) => ({ ...prev, captchaInput: '' }));
	};

	const validate = () => {
		const nextErrors = {};

		if (!form.fullName.trim()) {
			nextErrors.fullName = 'Le nom complet est obligatoire.';
		}

		if (!form.email.trim()) {
			nextErrors.email = "L'adresse e-mail est obligatoire.";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			nextErrors.email = 'Adresse e-mail invalide.';
		}

		if (!form.phone) {
			nextErrors.phone = 'Le téléphone est obligatoire.';
		} else if (!isValidPhoneNumber(form.phone)) {
			nextErrors.phone = 'Numéro de téléphone invalide.';
		}

		if (!form.level) {
			nextErrors.level = "Veuillez Niveau d'études.";
		}

		if (!form.track) {
			nextErrors.track = 'Le parcours est obligatoire.';
		}

		if (!form.captchaInput.trim()) {
			nextErrors.captchaInput = 'Veuillez saisir le captcha.';
		} else if (form.captchaInput.trim().toUpperCase() !== captcha) {
			nextErrors.captchaInput = 'Captcha incorrect.';
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const resetForm = () => {
		setForm({
			fullName: '',
			email: '',
			phone: '',
			level: '',
			track: defaultTrackValue,
			captchaInput: '',
		});
		setErrors({});
		setCaptcha(generateCaptcha());
	};

	const buildOdooPayload = () => {
		return {
			params: {
				name: form.fullName.trim(),
				email: form.email.trim(),
				phone: form.phone,
				parcours: form.track,
				niveau_scolaire: form.level,
				tag_ids: DEFAULT_TAG,
			},
		};
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validate()) return;

		setIsSubmitting(true);
		setSubmitMessage({ type: '', text: '' });

		const apiUrl =
			process.env.REACT_APP_API_URL ||
			'https://portal.clevory.info/testwordpress';

		try {
			await postJson(
				apiUrl,
				buildOdooPayload(),
				"Erreur lors de l'envoi du formulaire.",
			);

			setSubmitMessage({
				type: 'success',
				text: '✅ Votre demande a bien été envoyée.',
			});
			resetForm();
		} catch (error) {
			console.error('Erreur API Odoo:', error);
			setSubmitMessage({
				type: 'error',
				text:
					error.message ||
					'❌ Une erreur est survenue. Veuillez réessayer plus tard.',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!programData || !programData.tabs || !programData.tabs.length) {
		return null;
	}

	return (
		<section className='program-tabs-section'>
			<div className='program-tabs-section__container'>
				<div className='program-tabs-section__layout'>
					<div className='program-tabs-section__left'>
						<h1 className='program-tabs-panel__top-badge'>
							{programData.pageTitle}
						</h1>

						<div
							className='program-tabs-tabs'
							role='tablist'
							aria-label='Programme tabs'>
							{programData.tabs.map((tab) => (
								<button
									key={tab.id}
									type='button'
									role='tab'
									aria-selected={activeTab === tab.id}
									className={`program-tabs-tabs__button ${
										activeTab === tab.id
											? 'program-tabs-tabs__button--active'
											: ''
									}`}
									onClick={() => setActiveTab(tab.id)}>
									<span className='program-tabs-tabs__wave' />
									<span className='program-tabs-tabs__button-text'>
										{tab.label}
									</span>
									<span className='program-tabs-tabs__button-line' />
								</button>
							))}
						</div>

						<div className='program-tabs-panel'>
							{activeContent && (
								<div
									key={activeContent.id}
									className='program-tabs-panel__content'>
									<div className='program-tabs-panel__header'>
										<h2 className='program-tabs-panel__title'>
											{activeContent.title}
										</h2>
										<p className='program-tabs-panel__subtitle'>
											<span
												dangerouslySetInnerHTML={{
													__html: activeContent.subtitle,
												}}
											/>
										</p>
									</div>

									<div className='program-accordion'>
										{activeContent.items.map((item) => {
											const isOpen = !!openItems[item.id];

											return (
												<div
													key={item.id}
													className={`program-accordion__item ${
														isOpen ? 'program-accordion__item--open' : ''
													}`}>
													<button
														type='button'
														className='program-accordion__trigger'
														onClick={() => toggleAccordion(item.id)}
														aria-expanded={isOpen}>
														<div className='program-accordion__trigger-left'>
															<span className='program-accordion__icon'>✓</span>
															<span className='program-accordion__trigger-text'>
																{item.title}
															</span>
														</div>

														<span className='program-accordion__arrow'>
															{isOpen ? '−' : '+'}
														</span>
													</button>

													<div
														className={`program-accordion__content ${
															isOpen ? 'program-accordion__content--open' : ''
														}`}>
														<div className='program-accordion__content-inner'>
															<p
																dangerouslySetInnerHTML={{
																	__html: item.content,
																}}
															/>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</div>
					</div>

					<div className='program-tabs-section__right'>
						<div className='program-contact-card'>
							<h3 className='program-contact-card__title'>Contact</h3>
							<p className='program-contact-card__info'>
								admission@clevoryacademy.com
							</p>
							<p className='program-contact-card__info'>(+216) 29 376 774</p>
						</div>

						<div className='program-contact-form'>
							<div className='program-contact-form__glow' />
							<h3 className='program-contact-form__title'>
								Envoyez-nous un message
							</h3>
							<p className='program-contact-form__subtitle'>
								Complétez le formulaire et notre équipe vous recontactera
								rapidement.
							</p>

							<form
								className='program-contact-form__form'
								onSubmit={handleSubmit}
								noValidate>
								<div className='program-contact-form__field'>
									<label>Nom complet *</label>
									<input
										type='text'
										value={form.fullName}
										onChange={(e) => handleChange('fullName', e.target.value)}
										className={errors.fullName ? 'is-error' : ''}
										placeholder='Votre nom complet'
									/>
									{errors.fullName && (
										<p className='program-contact-form__error'>
											{errors.fullName}
										</p>
									)}
								</div>

								<div className='program-contact-form__field'>
									<label>Adresse e-mail *</label>
									<input
										type='email'
										value={form.email}
										onChange={(e) => handleChange('email', e.target.value)}
										className={errors.email ? 'is-error' : ''}
										placeholder='exemple@email.com'
									/>
									{errors.email && (
										<p className='program-contact-form__error'>
											{errors.email}
										</p>
									)}
								</div>

								<div className='program-contact-form__field'>
									<label>Téléphone *</label>
									<div
										className={`program-contact-form__phone ${
											errors.phone ? 'is-error' : ''
										}`}>
										<PhoneInput
											international
											defaultCountry='TN'
											countryCallingCodeEditable={false}
											placeholder='Numéro de téléphone'
											value={form.phone}
											onChange={(value) => handleChange('phone', value || '')}
											className='program-contact-form__phone-input'
										/>
									</div>
									{errors.phone && (
										<p className='program-contact-form__error'>
											{errors.phone}
										</p>
									)}
								</div>

								<div className='program-contact-form__field'>
									<label>Niveau scolaire *</label>
									<select
										value={form.level}
										onChange={(e) => handleChange('level', e.target.value)}
										className={errors.level ? 'is-error' : ''}>
										{studyLevels.map((item) => (
											<option key={item.value || item.label} value={item.value}>
												{item.label}
											</option>
										))}
									</select>
									{errors.level && (
										<p className='program-contact-form__error'>
											{errors.level}
										</p>
									)}
								</div>

								<div className='program-contact-form__field'>
									<label>Programme d'intérêt *</label>
									<select
										value={form.track}
										onChange={(e) => handleChange('track', e.target.value)}
										className={errors.track ? 'is-error' : ''}>
										{visibleProgramTracks.map((track) => (
											<option
												key={track.value || track.label}
												value={track.value}>
												{track.label}
											</option>
										))}
									</select>
									{errors.track && (
										<p className='program-contact-form__error'>
											{errors.track}
										</p>
									)}
								</div>

								<div className='program-contact-form__field'>
									<label>Code de vérification *</label>

									<div className='program-contact-form__verification'>
										<input
											type='text'
											placeholder='Entrer le code'
											value={form.captchaInput}
											onChange={(e) =>
												handleChange(
													'captchaInput',
													e.target.value.toUpperCase(),
												)
											}
											className={errors.captchaInput ? 'is-error' : ''}
											autoComplete='off'
										/>
										<div className='program-contact-form__captcha'>
											{captcha}
										</div>
										<button
											type='button'
											className='program-contact-form__refresh'
											aria-label='Refresh verification code'
											onClick={refreshCaptcha}>
											<RefreshCw size={16} />
										</button>
									</div>
									{errors.captchaInput && (
										<p className='program-contact-form__error'>
											{errors.captchaInput}
										</p>
									)}
								</div>

								{submitMessage.text && (
									<div
										className={`program-contact-form__alert program-contact-form__alert--${submitMessage.type}`}>
										{submitMessage.text}
									</div>
								)}

								<button
									type='submit'
									className='program-contact-form__submit'
									disabled={isSubmitting}>
									{isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
									<span>➜</span>
								</button>
							</form>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
